/**
 * Builder Fields Overlay
 *
 * Renders user-created form fields (text fields and checkboxes) when form builder is active
 * Supports drag, resize, and selection
 */

import React, { useState, useCallback, useRef } from 'react';
import styles from './BuilderFieldsOverlay.module.scss';

interface BuilderFormField {
  id: string;
  type: 'textfield' | 'checkbox';
  pageNum: number;
  x: number;
  y: number;
  width: number;
  height: number;
  placeholder?: string;
  multiline?: boolean;
  horizontalAlign?: 'left' | 'center' | 'right';
  verticalAlign?: 'top' | 'middle' | 'bottom';
  fontSize?: number;
  fontColor?: string;
}

export interface BuilderFieldsOverlayProps {
  pageNum: number;
  pageWidth: number;
  pageHeight: number;
  fields: BuilderFormField[];
  selectedFieldId: string | null;
  zoom: number;
  readOnly?: boolean; // When true, disable dragging, resizing, and selection
  onFieldClick?: (fieldId: string, x: number, y: number) => void;
  onFieldSelect?: (fieldId: string) => void;
  onFieldPositionChange?: (fieldId: string, x: number, y: number) => void;
  onFieldSizeChange?: (fieldId: string, width: number, height: number) => void;
}

export const BuilderFieldsOverlay: React.FC<BuilderFieldsOverlayProps> = ({
  pageNum,
  pageWidth,
  pageHeight,
  fields,
  selectedFieldId,
  zoom,
  readOnly = false,
  onFieldClick,
  onFieldSelect,
  onFieldPositionChange,
  onFieldSizeChange,
}) => {
  const [dragging, setDragging] = useState<{ fieldId: string; startX: number; startY: number } | null>(null);
  const [resizing, setResizing] = useState<{ fieldId: string; corner: string; startX: number; startY: number; startWidth: number; startHeight: number } | null>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  const pageFields = fields.filter(f => f.pageNum === pageNum);

  /**
   * Handle field click for context menu (disabled in read-only mode)
   */
  const handleFieldClick = useCallback((e: React.MouseEvent, fieldId: string) => {
    if (readOnly) return; // Disable clicks in read-only mode
    e.stopPropagation();
    if (onFieldSelect) {
      onFieldSelect(fieldId);
    }
    if (onFieldClick) {
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX;
      const y = rect.bottom + 5;
      onFieldClick(fieldId, x, y);
    }
  }, [readOnly, onFieldClick, onFieldSelect]);

  /**
   * Handle drag start (disabled in read-only mode)
   */
  const handleDragStart = useCallback((e: React.MouseEvent, fieldId: string) => {
    if (readOnly) return; // Disable dragging in read-only mode
    e.stopPropagation();
    if (e.button !== 0) return; // Only left click

    setDragging({
      fieldId,
      startX: e.clientX,
      startY: e.clientY,
    });
  }, [readOnly]);

  /**
   * Handle resize start
   */
  const handleResizeStart = useCallback((e: React.MouseEvent, fieldId: string, corner: string) => {
    e.stopPropagation();
    if (e.button !== 0) return;

    const field = fields.find(f => f.id === fieldId);
    if (!field) return;

    setResizing({
      fieldId,
      corner,
      startX: e.clientX,
      startY: e.clientY,
      startWidth: field.width,
      startHeight: field.height,
    });
  }, [fields]);

  /**
   * Handle mouse move for drag and resize
   */
  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (dragging && onFieldPositionChange) {
      const field = fields.find(f => f.id === dragging.fieldId);
      if (!field) return;

      // Convert screen pixel delta to PDF point delta by dividing by zoom
      const deltaX = (e.clientX - dragging.startX) / zoom;
      const deltaY = (e.clientY - dragging.startY) / zoom;

      const newX = Math.max(0, Math.min(pageWidth - field.width, field.x + deltaX));
      const newY = Math.max(0, Math.min(pageHeight - field.height, field.y + deltaY));

      onFieldPositionChange(dragging.fieldId, newX, newY);

      setDragging({
        ...dragging,
        startX: e.clientX,
        startY: e.clientY,
      });
    }

    if (resizing && onFieldSizeChange) {
      const field = fields.find(f => f.id === resizing.fieldId);
      if (!field) return;

      // Convert screen pixel delta to PDF point delta by dividing by zoom
      const deltaX = (e.clientX - resizing.startX) / zoom;
      const deltaY = (e.clientY - resizing.startY) / zoom;

      let newWidth = resizing.startWidth;
      let newHeight = resizing.startHeight;

      // Calculate new size based on corner
      if (resizing.corner.includes('e')) {
        // Allow smaller minimum for checkboxes (8px to match initial offset)
        const minWidth = field.type === 'checkbox' ? 8 : 20;
        newWidth = Math.max(minWidth, resizing.startWidth + deltaX);
      } else if (resizing.corner.includes('w')) {
        const minWidth = field.type === 'checkbox' ? 8 : 20;
        newWidth = Math.max(minWidth, resizing.startWidth - deltaX);
      }

      if (resizing.corner.includes('s')) {
        // For text fields, minimum height is just the font size (no padding/margins)
        // For checkboxes, allow 6px minimum (smaller than default 16px)
        const minHeight = field.type === 'checkbox'
          ? 6
          : (field.fontSize || 12);
        newHeight = Math.max(minHeight, resizing.startHeight + deltaY);
      } else if (resizing.corner.includes('n')) {
        const minHeight = field.type === 'checkbox'
          ? 6
          : (field.fontSize || 12);
        newHeight = Math.max(minHeight, resizing.startHeight - deltaY);
      }

      // Clamp to max values
      newWidth = Math.min(600, newWidth);
      newHeight = Math.min(200, newHeight);

      onFieldSizeChange(resizing.fieldId, newWidth, newHeight);
      // Don't update resizing state - keep original start values for proper delta calculation
    }
  }, [dragging, resizing, fields, pageWidth, pageHeight, zoom, onFieldPositionChange, onFieldSizeChange]);

  /**
   * Handle mouse up to end drag/resize
   */
  const handleMouseUp = useCallback(() => {
    setDragging(null);
    setResizing(null);
  }, []);

  /**
   * Set up global mouse listeners for drag/resize
   */
  React.useEffect(() => {
    if (dragging || resizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [dragging, resizing, handleMouseMove, handleMouseUp]);

  return (
    <div
      ref={overlayRef}
      className={styles.overlay}
      style={{ width: pageWidth, height: pageHeight }}
    >
      {pageFields.map(field => {
        const isSelected = selectedFieldId === field.id;

        // Apply zoom scaling to field dimensions
        const scaledX = field.x * zoom;
        const scaledY = field.y * zoom;
        const scaledWidth = field.width * zoom;
        const scaledHeight = field.height * zoom;

        return (
          <div key={field.id}>
            <div
              data-field-id={field.id}
              className={`${styles.field} ${styles[field.type]} ${isSelected ? styles.selected : ''} ${readOnly ? styles.readOnly : ''}`}
              style={{
                left: `${scaledX}px`,
                top: `${scaledY}px`,
                width: `${scaledWidth}px`,
                height: `${scaledHeight}px`,
                // Checkboxes always centered, text fields use alignment properties
                textAlign: field.type === 'checkbox' ? 'center' : (field.horizontalAlign || 'left'),
                display: 'flex',
                alignItems: field.type === 'checkbox' ? 'center' : (field.verticalAlign === 'top' ? 'flex-start' : field.verticalAlign === 'bottom' ? 'flex-end' : 'center'),
                justifyContent: field.type === 'checkbox' ? 'center' : (field.horizontalAlign === 'left' ? 'flex-start' : field.horizontalAlign === 'right' ? 'flex-end' : 'center'),
                cursor: readOnly ? 'default' : 'move',
              }}
              onClick={(e) => handleFieldClick(e, field.id)}
              onMouseDown={(e) => handleDragStart(e, field.id)}
              title={field.placeholder || field.type}
            >
              {field.type === 'textfield' && (
                <div
                  className={styles.textFieldContent}
                  style={{
                    fontSize: field.fontSize ? `${field.fontSize}px` : undefined,
                    color: field.fontColor || undefined,
                  }}
                >
                  {field.placeholder || 'Text field'}
                </div>
              )}
              {field.type === 'checkbox' && (
                <div className={styles.checkboxContent} />
              )}
            </div>

            {/* Resize handles when selected (hidden in read-only mode) */}
            {isSelected && !readOnly && (
              <>
                <div
                  className={`${styles.resizeHandle} ${styles.nw}`}
                  style={{ left: `${scaledX - 4}px`, top: `${scaledY - 4}px` }}
                  onMouseDown={(e) => handleResizeStart(e, field.id, 'nw')}
                />
                <div
                  className={`${styles.resizeHandle} ${styles.ne}`}
                  style={{ left: `${scaledX + scaledWidth - 4}px`, top: `${scaledY - 4}px` }}
                  onMouseDown={(e) => handleResizeStart(e, field.id, 'ne')}
                />
                <div
                  className={`${styles.resizeHandle} ${styles.sw}`}
                  style={{ left: `${scaledX - 4}px`, top: `${scaledY + scaledHeight - 4}px` }}
                  onMouseDown={(e) => handleResizeStart(e, field.id, 'sw')}
                />
                <div
                  className={`${styles.resizeHandle} ${styles.se}`}
                  style={{ left: `${scaledX + scaledWidth - 4}px`, top: `${scaledY + scaledHeight - 4}px` }}
                  onMouseDown={(e) => handleResizeStart(e, field.id, 'se')}
                />
              </>
            )}
          </div>
        );
      })}
    </div>
  );
};
